import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';
import { useClients } from '../clients/useData';
/** Normalize API response to array (backend may return array or { data: [] } / { records: [] }) */
function toListArray<T = any>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray((raw as { data: T[] }).data)) {
    return (raw as { data: T[] }).data;
  }
  if (raw && typeof raw === 'object' && 'records' in raw && Array.isArray((raw as { records: T[] }).records)) {
    return (raw as { records: T[] }).records;
  }
  return [];
}

export interface Task {
  taskId: number;
  clientId?: number;
  taskAssignedFrom?: number;
  taskAssignedTo?: number;
  taskRelatedToType?: number;
  taskRelatedTo?: number;
  projectId?: number;
  supportTicketId?: number;
  taskSubject?: string;
  taskDescription?: string;
  taskDate?: string;
  taskStartDate?: string;
  taskEstimatedDate?: string;
  taskEndDate?: string;
  taskCategoryId?: number;
  taskTypeId?: number;
  taskSpentMin?: number;
  taskInvoicedMin?: number;
  taskPriority?: number;
  taskNotes?: string;
  taskCommonNote?: string;
  taskStatus?: number;
  readAt?: string;
  taskCreatedBy?: number;
  taskUpdatedBy?: number;
  taskCreatedAt?: string;
  taskUpdatedAt?: string;
  // Related data from includes
  client?: { clientName?: string };
  taskAssignedFromUser?: { userFirstName?: string; userDisplayName?: string; id?: number };
  taskAssignedToUser?: { userFirstName?: string; userDisplayName?: string; id?: number };
  taskCategoryDescr?: { tc_descr?: string };
  taskTypeDescr?: { tt_descr?: string };
  taskPriorityDescr?: { p_descr?: string };
  taskStatusDescr?: { ts_descr?: string; ts_color?: string };
  // Optional relations for list/detail display
  project?: { projectName?: string };
  supportTicket?: { id?: number; reference?: string; title?: string };
  FileAttachment?: Array<{
    id: number;
    fileName: string;
    filePath: string;
    fileType: string;
    fileSize: number;
    uploadedAt?: string;
  }>;
  taskTags?: Array<{ id: number; name: string; color: string }>;
}

export interface TaskStatusOptionRecord {
  id: number;
  ts_descr?: string;
  ts_color?: string;
}

export interface TasksResponse {
  records: Task[];
  myOpenTasksCount: number;
  inProgressCount: number;
  awaitingFeedbackCount: number;
  testingCount: number;
  notStartedCount: number;
}

export const useTasks = (searchParams?: string, filter?: string) => {
  let queryString = '';
  if (searchParams || filter) {
    const params = new URLSearchParams();
    if (searchParams) params.append('searchParams', searchParams);
    if (filter) params.append('filter', filter);
    queryString = `?${params.toString()}`;
  }

  const { data, error, isLoading, mutate } = useSWR<TasksResponse>(
    `${moduleConfig.module.api.list}${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data?.records || [], // GenericListPage expects 'data' property
    tasks: data?.records || [], // Keep for backward compatibility
    counts: {
      myOpenTasksCount: data?.myOpenTasksCount || 0,
      inProgressCount: data?.inProgressCount || 0,
      awaitingFeedbackCount: data?.awaitingFeedbackCount || 0,
      testingCount: data?.testingCount || 0,
      notStartedCount: data?.notStartedCount || 0,
    },
    total: data?.records?.length || 0,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useTask = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<Task>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    data: data, // GenericDetailPage expects 'data' property
    task: data, // Keep for backward compatibility
    isLoading,
    isError: error,
    mutate,
  };
};

// Hook to get clients for select dropdown
export const useClientsForSelect = () => {
  const { clients, isLoading } = useClients();
  
  return {
    clients: clients || [],
    isLoading,
    options: (clients || []).map((client) => ({
      value: String(client.clientId),
      label: client.clientName || `Client #${client.clientId}`,
    })),
  };
};

// Hook to get users for select dropdown
export const useUsersForSelect = () => {
  const { data, error, isLoading } = useSWR<any>(
    '/users',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const users = toListArray(data);
  return {
    users,
    isLoading,
    isError: error,
    options: users.map((user) => ({
      value: String(user.id),
      label: user.userDisplayName || `${user.userFirstName || ''} ${user.userLastName || ''}`.trim() || `User #${user.id}`,
    })),
  };
};

// Hook to get task categories for select dropdown
export const useTaskCategoriesForSelect = () => {
  const { data, error, isLoading } = useSWR<any>(
    '/p-crm-task-category',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const taskCategories = toListArray(data);
  return {
    taskCategories,
    isLoading,
    isError: error,
    options: taskCategories.map((category) => ({
      value: String(category.id),
      label: category.tc_descr || `Category #${category.id}`,
    })),
  };
};

// Hook to get task types for select dropdown
export const useTaskTypesForSelect = () => {
  const { data, error, isLoading } = useSWR<any>(
    '/p-crm-task-type',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const taskTypes = toListArray(data);
  return {
    taskTypes,
    isLoading,
    isError: error,
    options: taskTypes.map((type) => ({
      value: String(type.id),
      label: type.tt_descr || `Type #${type.id}`,
    })),
  };
};

// Hook to get priorities for select dropdown
export const usePrioritiesForSelect = () => {
  const { data, error, isLoading } = useSWR<any>(
    '/p-crm-priority',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const priorities = toListArray(data);
  return {
    priorities,
    isLoading,
    isError: error,
    options: priorities.map((priority) => ({
      value: String(priority.id),
      label: priority.p_descr || `Priority #${priority.id}`,
    })),
  };
};

// Hook to get task statuses for select dropdown
export const useTaskStatusesForSelect = () => {
  const { data, error, isLoading } = useSWR<TaskStatusOptionRecord[]>(
    '/p-crm-task-status',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const taskStatuses = toListArray<TaskStatusOptionRecord>(data);
  return {
    taskStatuses,
    isLoading,
    isError: error,
    options: taskStatuses.map((status) => ({
      value: String(status.id),
      label: status.ts_descr || `Status #${status.id}`,
    })),
  };
};

// Hook to get projects for select dropdown (filtered by client)
export const useProjectsForSelect = (clientId?: number) => {
  const { data, error, isLoading } = useSWR<any>(
    '/crm-projects',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const allProjects = toListArray(data);
  const projects = clientId
    ? allProjects.filter((project: any) => project.clientId === clientId)
    : allProjects;

  return {
    projects,
    isLoading,
    isError: error,
    options: projects.map((project: any) => ({
      value: String(project.id),
      label: project.projectName || `Project #${project.id}`,
    })),
  };
};

// Hook to get support tickets for select dropdown (filtered by client)
export const useSupportTicketsForSelect = (clientId?: number) => {
  const { data, error, isLoading } = useSWR<any>(
    '/support-ticket',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const allTickets = toListArray(data);
  const tickets = clientId
    ? allTickets.filter((ticket: any) => ticket.clientId === clientId)
    : allTickets;

  return {
    tickets,
    isLoading,
    isError: error,
    options: tickets.map((ticket: any) => ({
      value: String(ticket.id),
      label: ticket.title || `Ticket #${ticket.id}`,
    })),
  };
};

// Hook to get tags for select dropdown
export const useTagsForSelect = () => {
  const { data, error, isLoading } = useSWR<any>(
    '/tags',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  const tags = toListArray(data);
  return {
    tags,
    isLoading,
    isError: error,
    options: tags.map((tag) => ({
      value: String(tag.id),
      label: tag.name || `Tag #${tag.id}`,
      color: tag.color,
    })),
  };
};

